import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import {
  AlertScopeType,
  UpsertDisconnectionAlertSettingRequest,
  CreateDisconnectionAlertScheduleRequest,
  UpdateDisconnectionAlertScheduleRequest,
  CreateDisconnectionAlertRecipientRequest,
  UpdateDisconnectionAlertRecipientRequest,
  CreateDisconnectionAlertActiveWindowRequest,
  UpdateDisconnectionAlertActiveWindowRequest,
  DisconnectionAlertSettingResponse,
  DisconnectionAlertScheduleResponse,
  DisconnectionAlertRecipientResponse,
  DisconnectionAlertActiveWindowResponse,
  DisconnectionAlertDeleteResponse,
  TriggerDisconnectionAlertResponse,
} from '@magnetar/admin-api-interfaces'

export const disconnectionAlertService = {
  // ============================================================
  // Settings API
  // ============================================================

  getSetting: async (scopeType: AlertScopeType, scopeId?: number, token?: string) => {
    try {
      const params = new URLSearchParams()
      params.append('scope_type', scopeType)
      if (scopeId !== undefined) params.append('scope_id', String(scopeId))

      const response = await apiClient.get<DisconnectionAlertSettingResponse>(
        `/api/disconnection-alert/settings?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  upsertSetting: async (data: UpsertDisconnectionAlertSettingRequest, token?: string) => {
    try {
      const response = await apiClient.put<DisconnectionAlertSettingResponse>(
        '/api/disconnection-alert/settings',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  // ============================================================
  // Schedules API
  // ============================================================

  createSchedule: async (data: CreateDisconnectionAlertScheduleRequest, token?: string) => {
    try {
      const response = await apiClient.post<DisconnectionAlertScheduleResponse>(
        '/api/disconnection-alert/schedules',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  updateSchedule: async (
    scheduleId: number,
    data: UpdateDisconnectionAlertScheduleRequest,
    token?: string,
  ) => {
    try {
      const response = await apiClient.put<DisconnectionAlertScheduleResponse>(
        `/api/disconnection-alert/schedules/${scheduleId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  deleteSchedule: async (scheduleId: number, token?: string) => {
    try {
      const response = await apiClient.delete<DisconnectionAlertDeleteResponse>(
        `/api/disconnection-alert/schedules/${scheduleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  // ============================================================
  // Recipients API
  // ============================================================

  createRecipient: async (data: CreateDisconnectionAlertRecipientRequest, token?: string) => {
    try {
      const response = await apiClient.post<DisconnectionAlertRecipientResponse>(
        '/api/disconnection-alert/recipients',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  updateRecipient: async (
    recipientId: number,
    data: UpdateDisconnectionAlertRecipientRequest,
    token?: string,
  ) => {
    try {
      const response = await apiClient.put<DisconnectionAlertRecipientResponse>(
        `/api/disconnection-alert/recipients/${recipientId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  deleteRecipient: async (recipientId: number, token?: string) => {
    try {
      const response = await apiClient.delete<DisconnectionAlertDeleteResponse>(
        `/api/disconnection-alert/recipients/${recipientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  // ============================================================
  // Active Windows API
  // ============================================================

  createActiveWindow: async (data: CreateDisconnectionAlertActiveWindowRequest, token?: string) => {
    try {
      const response = await apiClient.post<DisconnectionAlertActiveWindowResponse>(
        '/api/disconnection-alert/active-windows',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  updateActiveWindow: async (
    windowId: number,
    data: UpdateDisconnectionAlertActiveWindowRequest,
    token?: string,
  ) => {
    try {
      const response = await apiClient.put<DisconnectionAlertActiveWindowResponse>(
        `/api/disconnection-alert/active-windows/${windowId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  deleteActiveWindow: async (windowId: number, token?: string) => {
    try {
      const response = await apiClient.delete<DisconnectionAlertDeleteResponse>(
        `/api/disconnection-alert/active-windows/${windowId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },

  // ============================================================
  // Manual Trigger API
  // ============================================================

  triggerManualCheck: async (alertSettingId: number, token?: string) => {
    try {
      const response = await apiClient.post<TriggerDisconnectionAlertResponse>(
        '/api/disconnection-alert/trigger',
        { alert_setting_id: alertSettingId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return { success: false, error: 'Unknown error' }
    }
  },
}
